import React, { useState, useEffect } from 'react';
import {
  Button,
  Space,
  Modal,
  Form,
  Input,
  Select,
  Tag,
  Card,
  Row,
  Col,
} from 'antd';
import {
  PlusOutlined,
  SafetyOutlined,
  EditOutlined,
  DeleteOutlined,
} from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import { Service } from '@/apis';
import type {
  UserPermissionBaseResData,
  UserPermissionSaveDto,
  UserPermissionPutDto,
} from '@/apis';
import {
  DataStats,
  DataTable,
  type DataStat,
  type ActionButton,
} from '@/components/CommonTable';
import { useDataTableWithSearch } from '@/hooks/useDataTableWithSearch';
import { useFormModal } from '@/hooks/useFormModal';
import { SearchBar } from '@/components/SearchBar';
import {
  createTimeColumn,
  updateTimeColumn,
  createLongTextColumn,
  getPermissionTypeText,
  getPermissionTypeColor,
  getHttpMethodColor,
} from '@/utils/commonUtils';
import styles from '@/components/CommonTable/index.module.less';

// 使用API返回的数据类型
type PermissionData = UserPermissionBaseResData;

const CustomerUserPermissionManagement: React.FC = () => {
  // 用于父级权限下拉选择的权限列表
  const [parentPermissionOptions, setParentPermissionOptions] = useState<
    PermissionData[]
  >([]);
  const [parentPermissionLoading, setParentPermissionLoading] = useState(false);

  // 跟踪当前选择的权限类型
  const [selectedPermissionType, setSelectedPermissionType] =
    useState<number>(1);

  // 使用带搜索功能的表格管理Hook
  const {
    data: permissions,
    loading,
    pagination,
    fetchData: fetchPermissions,
    handleDelete,
    handlePaginationChange,
    search,
  } = useDataTableWithSearch<PermissionData>({
    fetchData: Service.userPermissionControllerGet,
    deleteData: async (id: number) => {
      await Service.userPermissionControllerDelete(id);
    },
    errorMessage: '获取权限列表失败',
    searchOptions: {
      fields: ['permissionName', 'permissionKey', 'permissionMark', 'sort'],
    },
  });

  // 获取父级权限选项
  const fetchParentPermissionOptions = async () => {
    setParentPermissionLoading(true);
    try {
      const response = await Service.userPermissionControllerGet(1, 1000); // 获取所有权限
      setParentPermissionOptions(response.data?.rows || []);
    } catch (error) {
      console.error('获取父级权限选项失败:', error);
    } finally {
      setParentPermissionLoading(false);
    }
  };

  // 使用Modal管理Hook
  const {
    visible,
    editingItem: editingPermission,
    form,
    handleAdd,
    handleEdit,
    handleOk,
    handleCancel,
  } = useFormModal<PermissionData>({
    createData: async (data: UserPermissionSaveDto) => {
      await Service.userPermissionControllerSave(data);
    },
    updateData: async (data: UserPermissionPutDto) => {
      await Service.userPermissionControllerPut(data);
    },
  });

  // 处理Modal确认后的数据刷新
  const handleModalOkWithRefresh = async () => {
    const success = await handleOk();
    if (success) {
      fetchPermissions();
      fetchParentPermissionOptions(); // 刷新父级权限选项
    }
  };

  // 组件加载时获取父级权限选项
  useEffect(() => {
    fetchParentPermissionOptions();
  }, []);

  // 当编辑权限时，同步权限类型状态
  useEffect(() => {
    if (editingPermission) {
      setSelectedPermissionType(editingPermission.permissionType);
    } else {
      setSelectedPermissionType(1); // 新增时默认为系统菜单
    }
  }, [editingPermission]);

  const columns: ColumnsType<PermissionData> = [
    createLongTextColumn('权限名称', 'permissionName', 12),
    {
      title: '权限标识',
      dataIndex: 'permissionKey',
      key: 'permissionKey',
      render: (key: string) => <Tag color='blue'>{key}</Tag>,
    },
    createLongTextColumn('路由地址', 'permissionMark', 20),
    {
      title: '权限类型',
      dataIndex: 'permissionType',
      key: 'permissionType',
      render: (type: number) => (
        <Tag color={getPermissionTypeColor(type)}>
          {getPermissionTypeText(type)}
        </Tag>
      ),
    },
    {
      title: '请求方式',
      dataIndex: 'permissionMethod',
      key: 'permissionMethod',
      render: (method: string) => (
        <Tag color={getHttpMethodColor(method)}>{method}</Tag>
      ),
    },
    {
      title: '排序',
      dataIndex: 'sort',
      key: 'sort',
      width: 80,
      sorter: (a, b) => a.sort - b.sort,
    },
    createLongTextColumn('父级权限', 'parentKey', 15),
    createTimeColumn('full'),
    updateTimeColumn('full'),
  ];

  // 统计卡片数据
  const stats: DataStat[] = [
    {
      title: '总权限数',
      value: pagination.total,
      prefix: <SafetyOutlined />,
      valueStyle: { color: '#3f8600' },
    },
    {
      title: '当前页权限',
      value: permissions.length,
      valueStyle: { color: '#1890ff' },
    },
    {
      title: '菜单权限',
      value: permissions.filter(p => p.permissionType === 1).length,
      valueStyle: { color: '#cf1322' },
    },
    {
      title: '按钮权限',
      value: permissions.filter(p => p.permissionType === 2).length,
      valueStyle: { color: '#722ed1' },
    },
  ];

  // 操作按钮配置
  const actionButtons: ActionButton<PermissionData>[] = [
    {
      key: 'edit',
      label: '编辑',
      icon: <EditOutlined />,
      onClick: handleEdit,
    },
    {
      key: 'delete',
      label: '删除',
      icon: <DeleteOutlined />,
      danger: true,
      onClick: record => handleDelete(record.permissionId),
      confirm: {
        title: '确定要删除这个权限吗？',
        okText: '确定',
        cancelText: '取消',
      },
    },
  ];

  return (
    <div className={styles.dataTablePage}>
      <Card className={styles.card}>
        {/* 统计卡片 */}
        <DataStats stats={stats} className={styles.statsRow} />

        {/* 操作栏 */}
        <div className={styles.toolbar}>
          <Space wrap>
            <Button type='primary' icon={<PlusOutlined />} onClick={handleAdd}>
              新增权限
            </Button>

            {/* 搜索区域 */}
            <SearchBar
              search={search}
              placeholder='搜索权限名称、权限键、API路径或排序值'
              width={300}
              loading={loading}
            />
          </Space>
        </div>

        {/* 权限表格 */}
        <DataTable
          columns={columns}
          dataSource={permissions}
          loading={loading}
          rowKey='permissionId'
          pagination={pagination}
          onPaginationChange={handlePaginationChange}
          actionButtons={actionButtons}
        />
      </Card>

      {/* 新增/编辑权限弹窗 */}
      <Modal
        title={editingPermission ? '编辑权限' : '新增权限'}
        open={visible}
        onOk={handleModalOkWithRefresh}
        onCancel={handleCancel}
        width={800}
      >
        <Form
          form={form}
          layout='vertical'
          initialValues={{
            status: 'active',
            permissionType: 1,
            permissionMethod: 'GET',
            sort: 1,
          }}
        >
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name='permissionName'
                label='权限名称'
                rules={[{ required: true, message: '请输入权限名称' }]}
              >
                <Input placeholder='请输入权限名称' />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name='permissionKey'
                label='权限标识'
                rules={[{ required: true, message: '请输入权限标识' }]}
              >
                <Input placeholder='请输入权限标识' />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name='permissionMark'
                label='路由地址'
                rules={[{ required: true, message: '请输入路由地址' }]}
              >
                <Input placeholder='请输入路由地址' />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name='permissionType'
                label='权限类型'
                rules={[{ required: true, message: '请选择权限类型' }]}
              >
                <Select
                  placeholder='请选择权限类型'
                  onChange={value => setSelectedPermissionType(value)}
                >
                  <Select.Option value={1}>系统菜单</Select.Option>
                  <Select.Option value={2}>按钮权限</Select.Option>
                  <Select.Option value={3}>接口权限</Select.Option>
                  <Select.Option value={4}>其他权限</Select.Option>
                </Select>
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            {selectedPermissionType === 3 && (
              <Col span={12}>
                <Form.Item
                  name='permissionMethod'
                  label='请求方式'
                  rules={[{ required: true, message: '请选择请求方式' }]}
                >
                  <Select placeholder='请选择请求方式'>
                    <Select.Option value='GET'>GET</Select.Option>
                    <Select.Option value='POST'>POST</Select.Option>
                    <Select.Option value='PUT'>PUT</Select.Option>
                    <Select.Option value='DELETE'>DELETE</Select.Option>
                  </Select>
                </Form.Item>
              </Col>
            )}
            <Col span={selectedPermissionType === 3 ? 12 : 24}>
              <Form.Item name='icon' label='图标'>
                <Input placeholder='请输入图标名称' />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item name='parentKey' label='父级权限'>
                <Select
                  placeholder='请选择父级权限'
                  allowClear
                  loading={parentPermissionLoading}
                  showSearch
                  filterOption={(input, option) =>
                    (option?.label ?? '')
                      .toLowerCase()
                      .includes(input.toLowerCase())
                  }
                  options={[
                    // 添加无父级权限选项
                    { value: '', label: '-' },
                    // 接口返回的权限数据
                    ...parentPermissionOptions
                      .filter(permission => {
                        // 排除接口权限类型（type为3）
                        if (permission.permissionType === 3) {
                          return false;
                        }
                        // 编辑时排除当前权限，避免循环引用
                        if (
                          editingPermission &&
                          permission.permissionId ===
                            editingPermission.permissionId
                        ) {
                          return false;
                        }
                        return true;
                      })
                      .map(permission => ({
                        value: permission.permissionKey,
                        label: `${permission.permissionName} (${permission.permissionKey})`,
                      })),
                  ]}
                />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name='sort'
                label='排序'
                rules={[{ required: true, message: '请输入排序值' }]}
              >
                <Input type='number' placeholder='请输入排序值' min={1} />
              </Form.Item>
            </Col>
          </Row>
        </Form>
      </Modal>
    </div>
  );
};

export default CustomerUserPermissionManagement;
